import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'
import DatePicker from "react-datepicker"

import FilterPagination from 'pages/parts/FilterPagination'
import useDidMountEffect from 'pages/parts/useDidMountEffect'

import { httpOk, GET } from 'helpers/api'
import { formatDate, formatMoney, returnSign } from "helpers/helpers"

function Index() {
	const { t } = useTranslation()
	const date = new Date()

	const [data, setData] = useState({})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		end_date: formatDate(new Date(), 'yyyy-MM-dd'),
	})
	const [pagination, setPagination] = useState({ url: '', response: null })

	async function searchWithFilter() {
		var filterUrl = "/inventory"
		var urlParams = ""

		if (filterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterData.start_date
		if (filterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterData.end_date
		if (filterData.page)
			urlParams += returnSign(urlParams) + 'page=' + filterData.page
		if (filterData.size)
			urlParams += returnSign(urlParams) + 'size=' + filterData.size

		setFilterDataExist(!!(filterData.start_date || filterData.end_date))
		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ url: filterUrl, response: response.data })
		}
		setDropdownIsOpen(false)
	}

	function resetFilter() {
		setFilterData({
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(), 'yyyy-MM-dd'),
		})
	}

	async function paginate(page, size) {
		setFilterData({ ...filterData, page, size })
	}

	useEffect(() => { searchWithFilter() }, []) // eslint-disable-line react-hooks/exhaustive-deps

	useDidMountEffect(() => { searchWithFilter() }, [filterData.page, filterData.size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					Inventarizatsiya [{data.total ?? 0}]
				</h4>
				<div className="d-flex gap-2">
					<Link className="btn-rounded btn btn-primary" to="/inventory/create">
						{t('create')}
					</Link>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex gap-2">
							<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide">
									{t('filter')}
								</Dropdown.Toggle>
								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('period')}</label>
											<div className="calendar-input">
												<DatePicker className="form-control" dateFormat="dd.MM.yyyy"
													selected={filterData.start_date ? new Date(filterData.start_date) : ''}
													onChange={d => setFilterData({ ...filterData, start_date: formatDate(d, 'yyyy-MM-dd') })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
										<div className="form-group col-md-6">
											<label className="invisible">.</label>
											<div className="calendar-input">
												<DatePicker className="form-control" dateFormat="dd.MM.yyyy"
													selected={filterData.end_date ? new Date(filterData.end_date) : ''}
													onChange={d => setFilterData({ ...filterData, end_date: formatDate(d, 'yyyy-MM-dd') })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12 d-flex justify-content-end gap-2">
											<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
											<button className="btn btn-primary" onClick={searchWithFilter}>{t('apply')}</button>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
						<div className="d-flex gap-3 vertical-center">
							<span className="text-danger fw-semibold">
								Taqchillik: {formatMoney(data.shortage ?? 0)}
							</span>
							<span className="text-success fw-semibold">
								Ortiqcha: {formatMoney(data.surplus ?? 0)}
							</span>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong><br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date && <span>{formatDate(filterData.start_date)}</span>}
										{filterData.start_date && filterData.end_date && <span> | </span>}
										{filterData.end_date && <span>{formatDate(filterData.end_date)}</span>}
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ minWidth: '33px' }} className="text-center">#</th>
									<th>Ombor</th>
									<th>{t('note')}</th>
									<th className="text-center" style={{ width: '100px' }}>Hujjat №</th>
									<th className="text-center" style={{ width: '160px' }}>{t('date')}</th>
									<th className="text-center" style={{ width: '100px' }}>{t('status')}</th>
									<th className="text-end" style={{ width: '130px' }}>Taqchillik</th>
									<th className="text-end" style={{ width: '130px' }}>Ortiqcha</th>
									<th style={{ minWidth: '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.row_number}</td>
										<td>{item.pos_name}</td>
										<td>{item.note ?? '—'}</td>
										<td className="text-center">{item.document_number}</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yyyy HH:mm')}</td>
										<td className={`text-center text-white ${item.completed ? 'bg-success' : 'bg-warning'}`}>
											{item.completed ? 'Yakunlangan' : 'Qoralama'}
										</td>
										<td className="text-end text-danger fw-semibold">
											{Number(item.total_shortage) < 0 ? formatMoney(Math.abs(item.total_shortage)) : '—'}
										</td>
										<td className="text-end text-success fw-semibold">
											{Number(item.total_surplus) > 0 ? formatMoney(item.total_surplus) : '—'}
										</td>
										<td className="d-flex justify-content-center">
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>
													<Dropdown.Menu>
														{item.completed ? (
															<Link to={`/inventory/preview/${item.id}`} className="table-dropdown-item">
																<i className="uil-eye"></i> {t('preview')}
															</Link>
														) : (
															<Link to={`/inventory/update/${item.id}`} className="table-dropdown-item">
																<i className="uil-edit-alt"></i> {t('edit')}
															</Link>
														)}
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination pagination={pagination} paginate={paginate} />
					}
				</div>
			</div>
		</>
	)
}

export default Index
